import type { FC, ReactNode } from "react";
import styled from "styled-components";
import { ProgressBar } from "../../ProgressBar/ProgressBar";
import { colorsPalettes } from "@similarweb/styles";
import { FlexRowFlexEnd } from "pro-features/styled components/StyledFlex/src/StyledFlex";
import { FlexRow } from "app/components/React/Table/cells/DomainListWithTooltip/styled";
import { TrafficShareTooltip } from "pro-features/components/tooltips/src/TrafficShareTooltip/TrafficShareTooltip";

const Value = styled.span`
    margin-right: 8px;
    flex: 0 0 auto;
    width: 40px;
`;
Value.displayName = "Value";

const ValueRight = styled(FlexRowFlexEnd)`
    width: 40px;
    flex: 0 0 auto;
    margin-left: 8px;
`;
ValueRight.displayName = "ValueRight";

const ProgressBarContainer = styled(FlexRow)`
    flex-grow: 1;
    flex-basis: 300px;
    .sw-progress-bar {
        align-self: center;
    }
`;

interface IGenericProgressBarrCellValue {
    leftValue: string | number;
    rightValue: string | number;
    fillPercent: number;
    leftValueName?: string;
    rightValueName?: string;
    isTooltipEnabled?: boolean;
}

interface IGenericProgressBarCellProps {
    value: IGenericProgressBarrCellValue;
    children?: ReactNode;
    height?: number;
}

ProgressBarContainer.displayName = "ProgressBarContainer";

export const GenericProgressBarCell: FC<IGenericProgressBarCellProps> = ({ value, height, children }) => {
    const { leftValue, rightValue, fillPercent, leftValueName, rightValueName, isTooltipEnabled } = value;

    const hasValues = !!leftValue && !!rightValue;
    const noDataText = "N/A";

    const progressBar = hasValues ? (
        <ProgressBarContainer>
            <Value>{leftValue}</Value>
            <ProgressBar
                width={fillPercent}
                isCompare={true}
                height={height}
            />
            <ValueRight>{rightValue}</ValueRight>
            {children}
        </ProgressBarContainer>
    ) : (
        <ProgressBarContainer>
            <Value>{noDataText}</Value>
            <ProgressBar
                width={0}
                innerColor={colorsPalettes.carbon[50]}
                isCompare={false}
            />
            <ValueRight>{noDataText}</ValueRight>
        </ProgressBarContainer>
    );

    if (hasValues && isTooltipEnabled) {
        const tooltipData = [
            {
                color: colorsPalettes.carbon[0],
                backgroundColor: colorsPalettes.blue[500],
                text: leftValue.toString(),
                width: 0.22,
                name: leftValueName,
            },
            {
                color: colorsPalettes.carbon[0],
                backgroundColor: colorsPalettes.sky[300],
                text: rightValue.toString(),
                width: 0.22,
                name: rightValueName,
            },
        ];

        return <TrafficShareTooltip trafficShareProps={tooltipData}>{progressBar}</TrafficShareTooltip>;
    }

    return progressBar;
};
GenericProgressBarCell.displayName = "GenericProgressBarCell";
